// Freehand-draw workspace page. Layout mirrors Studio for visual consistency:
// topbar + left rail + 3D canvas + right inspector.

import React, { useCallback, useRef, useState } from 'react';
import FreehandViewport from '@/components/freehand/FreehandViewport';
import FreehandTopbar from '@/components/freehand/FreehandTopbar';
import FreehandLeftRail from '@/components/freehand/FreehandLeftRail';
import FreehandRightInspector from '@/components/freehand/FreehandRightInspector';
import FreehandExportDialog from '@/components/freehand/FreehandExportDialog';
import FreehandGalleryDrawer from '@/components/freehand/FreehandGalleryDrawer';
import FreehandPaywall from '@/components/freehand/FreehandPaywall';
import FreehandEditToolbar from '@/components/freehand/FreehandEditToolbar';
import UpgradeDialog from '@/components/studio/UpgradeDialog';
import { useAuth } from '@/lib/auth';

export default function FreehandStudio() {
  const { isFreehandPro, loading } = useAuth();
  const [exportOpen, setExportOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const captureRef = useRef(null);
  const handleReady = useCallback((fn) => { captureRef.current = fn; }, []);
  const captureFn = useCallback(() => (captureRef.current ? captureRef.current() : null), []);

  // Full-page paywall gate. We wait for the auth check to resolve so a paying
  // user isn't briefly flashed the paywall while /auth/me is in flight.
  if (loading) {
    return (
      <div
        className="h-screen w-screen flex items-center justify-center text-slate-500"
        data-testid="freehand-loading"
      >
        Loading…
      </div>
    );
  }
  if (!isFreehandPro) return <FreehandPaywall />;

  return (
    <div className="h-screen w-screen overflow-hidden flex flex-col" data-testid="freehand-root">
      <FreehandTopbar
        onExport={() => setExportOpen(true)}
        onOpenUpgrade={() => setUpgradeOpen(true)}
        onOpenGallery={() => setGalleryOpen(true)}
        captureFn={captureFn}
      />
      <div className="flex-1 flex overflow-hidden">
        <aside className="hidden lg:flex w-[260px] shrink-0 min-h-0 border-r border-slate-200 bg-white" data-testid="freehand-rail-left">
          <FreehandLeftRail />
        </aside>
        <main className="relative flex-1 studio-canvas grain">
          <FreehandEditToolbar />
          <FreehandViewport
            onReady={handleReady}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
          />
          <div className="hidden sm:block absolute bottom-4 left-4 studio-glass px-3 py-2 rounded-xl text-xs text-slate-600 font-mono pointer-events-none">
            drag on ball = draw · release closes loop · drag empty space = orbit
          </div>
        </main>
        <aside className="hidden lg:flex w-[320px] shrink-0 border-l border-slate-200 bg-white" data-testid="freehand-rail-right">
          <FreehandRightInspector selectedId={selectedId} setSelectedId={setSelectedId} />
        </aside>
      </div>
      <FreehandExportDialog
        open={exportOpen}
        onOpenChange={setExportOpen}
        captureFn={captureFn}
        onNeedsUpgrade={() => { setExportOpen(false); setUpgradeOpen(true); }}
      />
      <FreehandGalleryDrawer open={galleryOpen} onOpenChange={setGalleryOpen} />
      <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} plan="freehand" />
    </div>
  );
}
